// DynamicTable.jsx
import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import { MoreHorizontal } from "lucide-react";
import SortableHeader from "./SortableHeader";
import ListLoadError from "./ListLoadError";

// Resize floors. Column defaults across the app average ~160px, so 120 leaves a
// text column readable while still allowing a real drag; using the average
// itself would stop a 150px-default column shrinking at all.
// Chip fields (rendered as pills by the status/type branches below) hold short
// labels like "Active", so they need less room than free text.
const MIN_COL_WIDTH_TEXT = 120;
const MIN_COL_WIDTH_CHIP = 100;
const CHIP_FIELDS = new Set([
  "status", "status_text", "listing_status",
  "os_type", "color", "ram", "storage",
]);
// Chips get a lower floor than text because their labels are short. A column
// whose chip needs more room than this sets col.minWidth explicitly — deriving
// the floor from col.width instead was wrong, because a generous default then
// stopped the column shrinking at all.
const minWidthFor = (col) =>
  col.minWidth || (CHIP_FIELDS.has(col.field) ? MIN_COL_WIDTH_CHIP : MIN_COL_WIDTH_TEXT);

const DynamicTable = ({
  columns = [],
  data = [],
  sortField,
  sortOrder,
  handleSort,
  onRowClick,
  onActionClick,
  noImage,
  selectedIds = [],
  dropdownOpenId,
  suppressHighlight = false,
  buttonRefs,
  rowKey = "id",

  searchTerm,
  selectedStatuses = [],
  selectedParentBrands = [],
  emptySearchImg,
  noBrandImage,
  emptyImage,
  emptyTitle,
  emptyMessage,
  showActions = true,
  showCheckbox = false,
  toggleSelect,
  toggleAll,
  stickyColumns = 1,
  resizableColumns = false,
  // The list request failed: show that (with Try again) instead of "No results"
  loadError = false,
  onRetry,
  errorWhat,
}) => {

  // 🔹 Check if table is empty
  const isEmpty = data.length === 0;
  const mouseDownRef = React.useRef(false);
  const isDraggingRef = React.useRef(false);

  // Measure actual th widths to compute left offsets for sticky columns
  const thRefs = useRef([]);
  const [stickyLefts, setStickyLefts] = useState([]);

  // 🔹 User-adjustable column widths (keyed by col.field, "__action__" for the action column)
  const [colWidths, setColWidths] = useState({});

  // Only show the sticky-column edge shadows when there's actually scrolled
  // content hidden behind them, not when the table sits flush/unscrolled.
  const scrollWrapperRef = useRef(null);
  const [scrollState, setScrollState] = useState({ left: false, right: false });

  // How much horizontal space is actually available before scrolling kicks
  // in. table-layout:fixed's native behavior for redistributing leftover
  // space (when the table's declared width exceeds the sum of its explicit
  // column widths) turned out to be unpredictable in practice — it dumped
  // almost all the extra space into one arbitrary column instead of
  // spreading it out, squeezing its neighbor. Computing the even split
  // ourselves avoids relying on that browser behavior.
  const [containerWidth, setContainerWidth] = useState(0);

  const updateScrollState = () => {
    const el = scrollWrapperRef.current;
    if (!el) return;
    setScrollState({
      left: el.scrollLeft > 0,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
    setContainerWidth(el.clientWidth);
  };

  useLayoutEffect(() => {
    updateScrollState();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, data, colWidths]);

  useEffect(() => {
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseWidthValue = (w) => {
    if (typeof w === "number") return w;
    if (typeof w === "string") {
      const n = parseFloat(w);
      return isNaN(n) ? 150 : n;
    }
    return 150;
  };

  // The floor is applied to sticky columns here, not just at drag time, so an
  // already-too-small width sitting in state gets corrected too — a sticky
  // column rendered very narrow triggers a browser quirk where its cells shrink
  // further while scrolling.
  const getColWidth = (col, isSticky = false) => {
    const raw = colWidths[col.field] ?? parseWidthValue(col.width);
    return isSticky ? Math.max(raw, minWidthFor(col)) : raw;
  };

  // The actual width to render for a column: its own getColWidth, plus an
  // even share of any leftover space (computed ourselves — see containerWidth
  // above for why we don't rely on the browser's native redistribution here).
  // Sticky columns are deliberately excluded from the stretch — every time a
  // sticky column's width has changed dynamically after its initial render,
  // the sticky-offset math has broken in some new way (measured-vs-rendered
  // drift, stale effect deps, etc.). Keeping sticky columns' widths fixed at
  // exactly getColWidth avoids that whole category of bug; only the
  // non-sticky columns absorb the leftover space.
  const getDisplayColWidth = (col, index) => {
    const isSticky = index < stickyColumns;
    const base = getColWidth(col, isSticky);
    if (isSticky || col.fixed || !containerWidth || columns.length === 0) return base;

   
    if (colWidths[col.field] !== undefined) return base;

    const stretchableCount = columns.filter(
      (c, i) => i >= stickyColumns && !c.fixed && colWidths[c.field] === undefined
    ).length;
    if (stretchableCount <= 0) return base;

    let baseTotal = showActions ? 56 : 0;
    if (showCheckbox) baseTotal += 50;
    for (let i = 0; i < columns.length; i++) {
      baseTotal += getColWidth(columns[i], i < stickyColumns);
    }

    const leftover = containerWidth - baseTotal;
    if (leftover <= 0) return base;

    return base + leftover / stretchableCount;
  };

  // Summed width of every column, used as the table's own width.
  //
  // It has to be an explicit pixel total, not 100% and not auto:
  //   - auto  => table-layout:fixed is ignored, so a column can never be
  //             dragged narrower than its widest cell.
  //   - 100%  => the table is pinned to the container, so widening one column
  //             can only come out of its neighbours (they get squished).
  // Summing the columns keeps the layout definite while letting the table grow
  // past the container and scroll instead of stealing from other columns.
  const getTableWidth = () => {
    let total = showActions ? 56 : 0;
    if (showCheckbox) total += 50;
    for (let i = 0; i < columns.length; i++) {
      total += getDisplayColWidth(columns[i], i);
    }
    return total;
  };

  // Slack between the summed columns and the wrapper. Once a column has been
  // dragged nothing stretches any more, so dragging one narrower makes the sum
  // fall short of the container and the table visibly pulls away from its right
  // edge, which reads as broken. This slack is rendered as an inert filler
  // column instead: the table stays full width, but the freed space is not
  // handed back to the other columns the way pinning the table to 100% would.
  const getFillerWidth = () => Math.max(0, containerWidth - getTableWidth());

  // Floors come from minWidthFor (see top of file). Callers always pass one;
  // the default here is just a backstop.
  const handleResizeMouseDown = (e, widthKey, startWidth, minWidth = MIN_COL_WIDTH_TEXT) => {
    e.preventDefault();
    e.stopPropagation();

    // Pin every column to the width it is rendering at right now, before the
    // drag starts. Until a column has an entry in colWidths it is still taking
    // a share of the leftover space, so the moment this drag pushes the total
    // past the container that share disappears and every other column snaps
    // back to its unstretched width — which looks like they are being squished
    // to pay for this one. Freezing them first means a drag only ever changes
    // the column being dragged; the table grows and scrolls instead.
    setColWidths((prev) => {
      const next = { ...prev };
      columns.forEach((c, i) => {
        if (next[c.field] === undefined) next[c.field] = getDisplayColWidth(c, i);
      });
      return next;
    });

    const startX = e.clientX;

    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      const newWidth = Math.max(minWidth, startWidth + delta);
      setColWidths((prev) => ({ ...prev, [widthKey]: newWidth }));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  // The z-index has to stay BELOW the sticky columns' own z-index (see
  // stickyStyle/stickyBase below). Non-sticky th/td don't establish their own
  // stacking context, so a handle's z-index "escapes" up to compete directly
  // against the sticky columns — a high value (e.g. 20) would let a
  // scrolled-away column's handle render on top of the sticky zone instead of
  // being covered by it like the rest of that column.
  const ResizeHandle = ({ widthKey, startWidth, minWidth }) => (
    <div
      className="col-resize-handle"
      onMouseDown={(e) => handleResizeMouseDown(e, widthKey, startWidth, minWidth)}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        right: 0,
        top: 0,
        height: "100%",
        width: "6px",
        cursor: "col-resize",
        zIndex: 2,
        userSelect: "none",
      }}
    />
  );

  useLayoutEffect(() => {
    if (stickyColumns < 1) return;
    const lefts = [];
    let cumulative = 0;
    for (let i = 0; i < stickyColumns; i++) {
      lefts[i] = cumulative;
      if (thRefs.current[i]) {
        // The -1 closes a small sub-pixel gap: offsetWidth rounds to the
        // nearest whole pixel, but the actual box can render at a fractional
        // width, so a naive sum of rounded widths can land the next sticky
        // column's left offset ~1px too far right, leaving a visible seam.
        cumulative += thRefs.current[i].offsetWidth - 1;
      }
    }
    setStickyLefts(lefts);
  // containerWidth is a dep because it drives getDisplayColWidth's leftover-
  // space stretch — without it, this can go stale: containerWidth starts at
  // 0, so the first render's sticky columns measure their un-stretched
  // width; once containerWidth updates and columns actually render wider,
  // this effect needs to re-measure or the offsets stay based on the
  // smaller pre-stretch sizes, letting an earlier (higher z-index) sticky
  // column visually cover most of the next one.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stickyColumns, columns, data, resizableColumns, colWidths, containerWidth]);

  // 🔹 Determine if filters are applied
  const filtersApplied =
    searchTerm ||
    (selectedStatuses?.length > 0) ||
    (selectedParentBrands?.length > 0);

  if (loadError) {
    return <ListLoadError what={errorWhat} onRetry={onRetry} />;
  }

  // 🔹 If empty → show empty-state UI and STOP rendering table
  if (isEmpty) {
    return (
      <div className="empty-state">
        <div className="empty-state-content">
          <div className="icon">
            <img
              src={emptyImage}
              alt="empty"
              style={{ width: 120, height: 120 }}
            />
          </div>

          <h1 className="no-item-title mt-2">{emptyTitle}</h1>

          <p className="no-item-text">{emptyMessage}</p>

        </div>
      </div>
    );
  }

  // The sticky Action column sits at right:0, but with fractional scroll
  // positions / fractional table widths the browser can leave a subpixel
  // hairline between the cell and the container's right edge, letting the
  // scrolling columns show through. This strip extends the cell's own
  // background 1px past its right edge to cover that sliver.
  const actionEdgeCover = (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        right: "-1px",
        width: "2px",
        background: "inherit",
        pointerEvents: "none",
      }}
    />
  );

  return (
    <div
      ref={scrollWrapperRef}
      onScroll={updateScrollState}
      style={{ width: "100%", overflowX: "auto" }}
    >
      <table
        className={`entities-table${scrollState.left ? " is-scrolled-left" : ""}${scrollState.right ? " is-scrolled-right" : ""}`}
        style={{
        
          // Resizable mode needs an explicit pixel total — see getTableWidth.
          width: resizableColumns ? `${getTableWidth() + getFillerWidth()}px` : "100%",
          tableLayout: resizableColumns ? "fixed" : "auto",
          borderCollapse: "separate",
          borderSpacing: 0,
          minWidth: "900px",
        }}
      >

        {resizableColumns && (
          // Declares each column's width authoritatively at the table level.
          // Individual sticky <th>/<td> cells can have their own width
          // recalculated by the browser mid-scroll (a real quirk with
          // position:sticky + table-layout:fixed) — colgroup sidesteps that by
          // making the column track width itself the source of truth instead.
          <colgroup>
            {showCheckbox && <col style={{ width: 50 }} />}
            {columns.map((col, index) => (
              <col key={col.field} style={{ width: `${getDisplayColWidth(col, index)}px` }} />
            ))}
            {getFillerWidth() > 0 && <col style={{ width: `${getFillerWidth()}px` }} />}
            {showActions && <col style={{ width: 56 }} />}
          </colgroup>
        )}

        {/* ---------- HEADER ---------- */}
        <thead>
          <tr>
            {/* CHECKBOX HEADER (optional) */}
            {showCheckbox && (
              <th
                style={{
                  width: 50,
                  position: "sticky",
                  left: 0,
                  zIndex: 5,
                }}
              >
                <input
                  type="checkbox"
                  checked={
                    data.length > 0 &&
                    selectedIds.length === data.length
                  }
                  onChange={toggleAll}
                />
              </th>
            )}
            {columns.map((col, index) => {
              const isSticky = index < stickyColumns;
              // The last sticky column is the one that draws the edge shadow,
              // so it needs a hook the stylesheet can reach at any stickyColumns
              // count — nth-child(n) only ever fits one page's layout.
              const isStickyEdge = isSticky && index === stickyColumns - 1;
              const stickyStyle = isSticky ? {
                position: "sticky",
                left: stickyLefts[index] ?? 0,
                zIndex: stickyColumns - index + 3,
              } : {};

              const colWidth = resizableColumns ? `${getDisplayColWidth(col, index)}px` : (col.width || "150px");

              const thStyle = {
                height: "45px",
                padding: "10px",
                verticalAlign: "middle",
                alignSelf: 'stretch',
                alignItems: 'center',
                gap: '12px',
                position: "relative",
                minWidth: colWidth,
                width: colWidth,
                // Headers never clipped their own content — dragging a column
                // narrower than its label's natural width let the label spill
                // outside the th's box into the next column, which (since
                // leftmost sticky columns paint on top) looked like the next
                // column's text was being covered/eaten from the left.
                // Exception: the last sticky column keeps overflow visible so
                // its edge shadow (an ::after that pokes out via right:-8px)
                // isn't clipped by this same rule.
                ...(resizableColumns && !isStickyEdge && { overflow: "hidden" }),
                ...stickyStyle,
                ...(!resizableColumns && col.field && (col.field === 'category' || col.field === 'category_name') && { maxWidth: '390px' }),
              };

              const resizeHandle = resizableColumns ? (
                <ResizeHandle
                  widthKey={col.field}
                  startWidth={getDisplayColWidth(col, index)}
                  minWidth={minWidthFor(col)}
                />
              ) : null;

              // SortableHeader renders its own <th>, so pass styles/ref directly into it
              // to avoid invalid nested <th> which breaks background rendering
              if (col.sortable) {
                return (
                  <SortableHeader
                    key={col.field}
                    label={col.label}
                    field={col.field}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    handleSort={handleSort}
                    innerRef={(el) => { if (isSticky) thRefs.current[index] = el; }}
                    style={thStyle}
                    classes={isStickyEdge ? "sticky-edge" : ""}
                    resizeHandle={resizeHandle}
                  />
                );
              }

              return (
                <th
                  key={col.field}
                  ref={(el) => { if (isSticky) thRefs.current[index] = el; }}
                  className={isStickyEdge ? "sticky-edge" : ""}
                  style={thStyle}
                >
                  {col.label}
                  {resizeHandle}
                </th>
              );
            })}

            {/* ACTION HEADER — not resizable, stakeholder wants it fixed-width */}
            {resizableColumns && getFillerWidth() > 0 && (
              // Inert: no label, no resize handle, not sortable. It exists only
              // to absorb the slack so the header row reaches the wrapper edge.
              <th aria-hidden="true" className="fillerHeader" />
            )}

            {showActions && (
              <th
                className="actionHeader"
                style={{
                  position: "sticky",
                  right: 0,
                  // background: "#F1F3F5",
                  textAlign: "center",
                  verticalAlign: "middle",
                  paddingLeft: 2,
                  paddingRight: 2,
                  zIndex: 4,
                }}
              >
                Action
                {actionEdgeCover}
              </th>
            )}
          </tr>
        </thead>


        {/* ---------- BODY ---------- */}
        <tbody>
          {data.map((row, idx) => {

            const rowBg = idx % 2 ? "#F8F9FA" : "#FFFFFF";

            const isRowSelected =
              (selectedIds.includes(row[rowKey]) ||
                dropdownOpenId === row[rowKey]) &&
              !suppressHighlight;

            return (
              // <tr
              //   key={row[rowKey] || idx}
              //   onClick={() => {
              //     console.log("Row clicked", row);
              //     onRowClick && onRowClick(row);
              //   }}

              //   className={[
              //     idx % 2 ? "striped" : "",
              //     isRowSelected ? "row-selected" : "",
              //   ].join(" ")}
              //   style={{
              //     height: "45px",
              //     cursor: onRowClick ? "pointer" : "default",
              //     background: rowBg,
              //   }}
              // >
              <tr
                key={row[rowKey] || idx}

                onMouseDown={() => {
                  mouseDownRef.current = true;
                  isDraggingRef.current = false;
                }}

                onMouseMove={() => {
                  if (mouseDownRef.current) {
                    isDraggingRef.current = true;
                  }
                }}

                onMouseUp={() => {
                  mouseDownRef.current = false;
                }}

                onClick={() => {
                  // 🚫 prevent click after drag
                  if (isDraggingRef.current) return;

                  onRowClick && onRowClick(row);
                }}

                className={[
                  idx % 2 ? "striped" : "",
                  isRowSelected ? "row-selected" : "",
                ].join(" ")}
                style={{
                  height: "45px",
                  cursor: onRowClick ? "pointer" : "default",
                  background: rowBg,
                }}
              >
                {/* ROW CHECKBOX (optional) */}
                {showCheckbox && (
                  <td
                    style={{
                      width: 50,
                      position: "sticky",
                      left: 0,
                      background: rowBg,
                      zIndex: 3,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row[rowKey])}
                      onChange={() => toggleSelect(row[rowKey])}
                    />
                  </td>
                )}

                {columns.map((col, index) => {
                  const isFirst = index === 0;
                  const isSticky = index < stickyColumns;
                  // See the matching comment in the header: the shadow is drawn
                  // on the last sticky column, and the cell must not clip it.
                  const isStickyEdge = isSticky && index === stickyColumns - 1;
                  const value = row[col.field];

                  // Same reasoning as the header's stickyStyle above.
                  const stickyBase = isSticky ? {
                    position: "sticky",
                    left: stickyLefts[index] ?? 0,
                    zIndex: stickyColumns - index + 2,
                  } : {};

                  const cellKey = col.field;

                  const maskLayer = isSticky ? (
                    <div
                      className="sticky-mask"
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: "100%",
                        height: "100%",
                        background: rowBg,
                        zIndex: -1,
                        pointerEvents: "none",
                      }}
                    />
                  ) : null;

                  // STATUS FIELD
                  if (col.field === "status" || col.field === "status_text"||col.field==="listing_status") {
                    return (
                      <td
                        key={cellKey}
                        className={isStickyEdge ? "sticky-edge" : ""}
                        style={{
                          position: "relative",
                          ...stickyBase,
                        }}
                      >
                        {maskLayer}
                        <span className={`status ${String(value || '').toLowerCase()}`}>
                          {value || "NA"}
                        </span>
                      </td>
                    );
                  }
                  // TYPE FIELD
                  if (col.field === "os_type" || col.field === "color" || col.field ==="ram" || col.field ==="storage") {
                    return (
                      <td
                        key={cellKey}
                        className={isStickyEdge ? "sticky-edge" : ""}
                        style={{
                          position: "relative",
                          ...stickyBase,
                        }}
                      >
                        <span
                          style={{
                            backgroundColor: "#FFEB00",
                            borderRadius: "99px",
                            padding: "5px 10px",
                            fontWeight: 600,
                            fontSize: "12px",
                            color: "#212529",
                            display: "inline-block",
                            whiteSpace: "nowrap",
                            textAlign: "center",
                          }}
                        >
                          {value || "NA"}
                        </span>
                      </td>
                    );
                  }


                  // NORMAL CELL
                  return (
                    <td
                      key={cellKey}
                      className={`${isFirst ? "firstColumn" : ""}${isStickyEdge ? " sticky-edge" : ""}`.trim()}
                      style={{
                        maxWidth: col.maxWidth || "none",
                        whiteSpace: "nowrap",
                        // Left off the shadow-drawing cell: an inline value here
                        // would outrank the stylesheet rule that has to open the
                        // cell up while scrolled. The cell still clips — the
                        // shared .entities-table th,td rule covers it — and the
                        // cell's inner wrapper truncates its own text regardless.
                        ...(isStickyEdge ? {} : { overflow: "hidden" }),
                        textOverflow: "ellipsis",
                        position: "relative",
                        ...stickyBase,

                        ...(!resizableColumns && col.field && (col.field == 'category' || col.field == 'category_name'))  && {
                          maxWidth: '390px',
                        }
                      }}
                    >
                      {maskLayer}

                      {col.render
                        ? col.render(row)
                        : value || <span style={{ color: "#ADB5BD" }}>NA</span>}
                    </td>
                  );
                })}

                {resizableColumns && getFillerWidth() > 0 && (
                  <td aria-hidden="true" className="fillerCell" style={{ background: rowBg }} />
                )}

                {/* ACTION CELL */}
                {showActions && (
                  <td
                    style={{
                      textAlign: "center",
                      position: "sticky",
                      right: 0,
                      background: rowBg,
                      zIndex: 3,
                    }}

                    className="actionColumn"
                  >
                    {actionEdgeCover}
                    <button
                      ref={(el) => buttonRefs && (buttonRefs.current[row[rowKey]] = el)}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        padding: 4,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onActionClick && onActionClick(row[rowKey],e)
                      }}
                    >
                      <MoreHorizontal size={20} />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>

      </table>
    </div>
  );
};

export default DynamicTable;
